import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { useLead } from "../../context/LeadContext";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export const Pricing = ({ compact = false }) => {
  const { leadInfo } = useLead();
  const [packages, setPackages] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    api
      .get("/pricing")
      .then(({ data }) => setPackages(data.packages))
      .catch(() => toast.error("Couldn't load pricing"));
  }, []);

  const checkout = async (pkg) => {
    setLoadingId(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
        email: leadInfo?.work_email || "",
        billing_period: billing,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout failed. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <section id="pricing" className={compact ? "" : "py-28 sm:py-32"} data-testid="pricing">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Pricing</span>
          <h2 className="mt-4 font-display text-4xl font-black tracking-tighter text-charcoal sm:text-5xl">
            Plans that pay for themselves in week one.
          </h2>
          <p className="mt-4 text-base text-silver-700">
            Every plan includes the AI WhatsApp agent, lead scoring and Google Sheets sync. Cancel anytime.
          </p>

          <div
            className="mx-auto mt-8 inline-flex items-center gap-1 rounded-full border border-silver-300/70 bg-silver-100 p-1"
            data-testid="billing-toggle"
          >
            <button
              data-testid="billing-monthly"
              onClick={() => setBilling("monthly")}
              className={`rounded-full px-5 py-2 text-sm font-bold transition ${
                billing === "monthly" ? "bg-white text-charcoal shadow-sm" : "text-silver-500 hover:text-charcoal"
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-annual"
              onClick={() => setBilling("annual")}
              className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition ${
                billing === "annual" ? "bg-white text-charcoal shadow-sm" : "text-silver-500 hover:text-charcoal"
              }`}
            >
              Annual
              <span className="rounded-full bg-brand px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {packages.map((pkg, i) => {
            const popular = !!pkg.popular;
            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative flex flex-col rounded-3xl border p-8 ${
                  popular
                    ? "border-brand bg-charcoal text-white shadow-[0_24px_60px_rgb(46,204,113,0.18)] md:-mt-4 md:mb-4"
                    : "border-silver-300/60 bg-white text-charcoal"
                }`}
                data-testid={`pricing-card-${pkg.id}`}
              >
                {popular && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-bold text-white">
                    <Sparkles className="h-3.5 w-3.5" /> Most popular
                  </span>
                )}
                <h3 className="font-display text-2xl font-extrabold tracking-tight">{pkg.name}</h3>
                <p className={`mt-1 text-sm ${popular ? "text-white/60" : "text-silver-500"}`}>
                  {pkg.tagline}
                </p>

                <div className="mt-6">
                  <div className="flex items-end gap-1">
                    <span className="font-display text-5xl font-black tracking-tighter" data-testid={`price-${pkg.id}`}>
                      ₹{(billing === "annual"
                        ? Math.round(pkg.price_annual / 12)
                        : pkg.price
                      ).toLocaleString("en-IN")}
                    </span>
                    <span className={`mb-1.5 text-sm ${popular ? "text-white/60" : "text-silver-500"}`}>
                      / month
                    </span>
                  </div>
                  <p className={`mt-1.5 text-xs font-medium ${popular ? "text-white/55" : "text-silver-500"}`}>
                    {billing === "annual" ? (
                      <>
                        ₹{pkg.price_annual.toLocaleString("en-IN")} billed yearly ·{" "}
                        <span className="font-bold text-brand">
                          save ₹{(pkg.price * 12 - pkg.price_annual).toLocaleString("en-IN")}
                        </span>
                      </>
                    ) : (
                      "billed monthly"
                    )}
                  </p>
                </div>

                <ul className="mt-7 space-y-3">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <span
                        className={`mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full ${
                          popular ? "bg-brand" : "bg-brand-light"
                        }`}
                      >
                        <Check className={`h-3 w-3 ${popular ? "text-white" : "text-brand-dark"}`} />
                      </span>
                      <span className={popular ? "text-white/85" : "text-silver-700"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`pricing-checkout-${pkg.id}`}
                  onClick={() => checkout(pkg)}
                  disabled={loadingId === pkg.id}
                  className={`mt-8 h-12 w-full text-base font-bold ${
                    popular
                      ? "bg-brand text-white hover:bg-brand-hover"
                      : "bg-charcoal text-white hover:bg-charcoal/90"
                  }`}
                >
                  {loadingId === pkg.id ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    `Start with ${pkg.name}`
                  )}
                </Button>
              </motion.div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-xs text-silver-500">
          Secure checkout by Stripe · Test mode. Use card 4242 4242 4242 4242
        </p>
      </div>
    </section>
  );
};

export default Pricing;
